module Client.ServiceCalls
open Client.Services
open Elmish
open Fable.Core
open Fable.Core.JsInterop
open Fetch.Types
open Shared
open System
open Thoth.Json
open Feliz
open Feliz.UseElmish
open Feliz.MaterialUI
open Fable.MaterialUI.Icons
open Fable.MaterialUI.MaterialDesignIcons
open Thoth.Fetch
open Fable.Core.Experimental
open Feliz.SweetAlert
open Fable.Core.JS
open Components.ServiceForm
open CustomerDexImaging.Models

let strOption (s : string) = if String.IsNullOrWhiteSpace s then None else Some s

let availableCallTypes = ServiceCallReason.getCases

type ServiceRequestViewModel =
  { EquipmentNumber : string
    Contact : ContactSelectionInfo
    ServiceCallReason : ServiceCallReason option
    ServiceCallReasonText : string
    Description : string
    PurchaseOrderNumber : string option }

type Msg =
  | EquipmentLookupResult of Result<Equipment,Components.EquipmentLookup.EquipmentLookupFailureResult>
  | EquipmentSelected of Shared.Equipment
  | ContactEntryCompleted of ContactSelectionInfo
  | PurchaseOrderNumberEntryCompleted of string
  | OpenServiceCallLookupResult of Result<ServiceCall list,string>
  | AcknowledgeExistingCalls
  | ServiceRequestUpdated of ServiceRequestViewModel
  | SetServiceCallReasonText of string
  | SelectServiceCallReason of ServiceCallReason
  | PlaceRequest of Shared.ServiceRequest
  | ServiceRequestResult of Result<string,string>
  | ResetState

type ContactEntryState =
  { Equipment : Shared.Equipment
    Contact : ContactSelectionInfo option }

type ServiceRequestEntryState =
  { Equipment : Shared.Equipment
    ServiceCalls : Shared.ServiceCall list option
    RequestInfo : ServiceRequestViewModel }

type ServiceCallCreationSuccessResultState =
  { QueueResponse : string
    ServiceRequestEntryState : ServiceRequestEntryState }

type OrderFailureResultState =
  { FailureMessage : string
    ServiceRequestEntryState : ServiceRequestEntryState }

type Model =
  | EquipmentLookup
  | ContactEntry of ContactEntryState
  | PurchaseOrderNumberEntry of ServiceRequestEntryState
  | ServiceCallLookup of ServiceRequestEntryState
  | ServiceRequestEntry of ServiceRequestEntryState
  | ExistingCallsWarning of ServiceRequestEntryState
  | PlacingServiceCall of ServiceRequestEntryState
  | RequestResult of ServiceCallCreationSuccessResultState
  | DowngradedForm of Components.ServiceForm.EntryState

let requestService (token, (request : Shared.ServiceRequest)) : Promise<string> =
  promise {
    let decoder = Services.decoder<string>
    let authHeader = HttpRequestHeaders.Authorization <| sprintf "Bearer %s" token
    return! Fetch.post<_,string>("/api/Service/Request", request, decoder = decoder,headers = [authHeader] )
  }

let requestServiceCmd token request =
  Cmd.OfPromise.either requestService (token, request)
    (Ok >> ServiceRequestResult)
    (fun x ->
      "Unable to request service for equipment. Please retry" |> Error |> ServiceRequestResult)

let getOpenCalls (token) : Promise<Shared.ServiceCall list> =
  promise {
    let decoder = Services.decoder<Shared.ServiceCall list>
    let authHeader = HttpRequestHeaders.Authorization <| sprintf "Bearer %s" token
    return! Fetch.get<_,Shared.ServiceCall list>("/api/Service/Pending", decoder = decoder,headers = [authHeader] )
  }

let getOpenCallsCmd token =
  Cmd.OfPromise.either getOpenCalls token
    (Ok >> OpenServiceCallLookupResult)
    (fun x ->
      "Unable to get open calls for equipment." |> Error |> OpenServiceCallLookupResult)

let init (equipmentOption) : Model * Cmd<Msg> =
  let cmd =
    match equipmentOption with
    | Some e ->
      (Ok e) |> EquipmentLookupResult |> Cmd.ofMsg
    | None ->
      Cmd.none
  EquipmentLookup, cmd

let update (msg : Msg) (model : Model) : Model * Cmd<Msg> =
  match model, msg with
  | _, ResetState ->
    EquipmentLookup, Cmd.none
  | EquipmentLookup, EquipmentLookupResult r ->
    match r with
    | Ok e -> model, Cmd.ofMsg (EquipmentSelected e)
    | Error v ->
      let failureMessage = v.toString()
      let equipmentId = v.LookupRequest.EquipmentOrSerialNumber
      DowngradedForm (Components.ServiceForm.EntryState.init equipmentId failureMessage), Cmd.none
  | EquipmentLookup, EquipmentSelected e ->
    ContactEntry { Equipment = e; Contact = None; }, Cmd.none
  | ContactEntry s, ContactEntryCompleted c ->
    let es= { ServiceRequestEntryState.Equipment = s.Equipment; ServiceCalls = None; RequestInfo = { EquipmentNumber = s.Equipment.EquipmentNumber; Description = ""; Contact = c; ServiceCallReason = None; ServiceCallReasonText = ""; PurchaseOrderNumber = None }}
    match s.Equipment.PurchaseOrderNumberRequired with
    | false ->
      ServiceCallLookup es, getOpenCallsCmd s.Equipment.Token
    | true ->
      PurchaseOrderNumberEntry es, Cmd.none
  | PurchaseOrderNumberEntry s, PurchaseOrderNumberEntryCompleted poNumber ->
    ServiceCallLookup { s with RequestInfo = { s.RequestInfo with PurchaseOrderNumber = Some poNumber }}, getOpenCallsCmd s.Equipment.Token
    //ServiceRequestEntry { ServiceRequestEntryState.Equipment = s.Equipment; RequestInfo = { EquipmentNumber = s.Equipment.Equipment.EquipmentNumber; Description = ""; Contact = c; ServiceCallReason = None; ServiceCallReasonText = ""; }}, Cmd.none
  | ServiceCallLookup s, OpenServiceCallLookupResult callResult ->
    match callResult with
    | Ok [] -> ServiceRequestEntry { s with ServiceCalls = Some [];}, Cmd.none
    | Ok calls -> ExistingCallsWarning { s with ServiceCalls = Some calls;}, Cmd.none
    | Error v -> ServiceRequestEntry { s with ServiceCalls = Some [];}, Cmd.none
  | ExistingCallsWarning s, AcknowledgeExistingCalls ->
    ServiceRequestEntry s, Cmd.none
  | ServiceRequestEntry s, ServiceRequestUpdated r ->
    ServiceRequestEntry { s with RequestInfo = r }, Cmd.none
  | ServiceRequestEntry s, SetServiceCallReasonText r ->
    ServiceRequestEntry { s with RequestInfo = { s.RequestInfo with ServiceCallReasonText = r } }, Cmd.none
  | ServiceRequestEntry s, SelectServiceCallReason r ->
    ServiceRequestEntry { s with RequestInfo = { s.RequestInfo with ServiceCallReason = Some r} }, Cmd.none
  | ServiceRequestEntry s, PlaceRequest r ->
    PlacingServiceCall s, requestServiceCmd s.Equipment.Token r
  | PlacingServiceCall s, ServiceRequestResult r ->
    match r with
    | Ok o ->
      RequestResult { QueueResponse = o; ServiceRequestEntryState = s; }, Cmd.none
    | Error msg ->
      let alert =
        Cmd.Swal.fire [
            swal.title "Unable to process request"
            swal.text "Please try the alternate request form." ]
      let entryState =
        { Components.ServiceForm.EntryState.FirstAndLastName =
            match s.RequestInfo.Contact with
            | ContactSelectionInfo.Contact _ -> FieldState.defaultValue
            | ContactSelectionInfo.NewContact c -> FieldState.init (Some (sprintf "%s %s" c.FirstName c.LastName))
          CompanyName = FieldState.defaultValue
          ServiceAddress = FieldState.defaultValue
          EmailAddress =
            match s.RequestInfo.Contact with
            | ContactSelectionInfo.Contact c -> FieldState.init (Some c.Email)
            | ContactSelectionInfo.NewContact c -> FieldState.init (Some c.Email)
          BusinessPhone =
            match s.RequestInfo.Contact with
            | ContactSelectionInfo.Contact _ -> FieldState.defaultValue
            | ContactSelectionInfo.NewContact c -> FieldState.init (Some c.Phone)
          EquipmentId = FieldState.init (Some s.Equipment.EquipmentNumber)
          Make = FieldState.defaultValue
          Model = FieldState.defaultValue
          Description = FieldState.defaultValue
          FailureMessage = Some msg }
      DowngradedForm entryState, alert
  | _ -> model, Cmd.none

let serviceRequestEntryView (model : ServiceRequestEntryState) dispatch =
  Html.div [
    Mui.grid [
      grid.container true
      grid.direction.column
      grid.spacing._2
      grid.children [
        Mui.grid [
          grid.item true
          grid.children [
            Html.form [
              prop.children [
                Mui.autocomplete [
                  autocomplete.options (availableCallTypes |> List.toArray)
                  autocomplete.getOptionLabel (fun (x : ServiceCallReason) -> sprintf "%s" x.friendlyDescription)
                  autocomplete.freeSolo false
                  autocomplete.fullWidth true
                  autocomplete.renderInput (fun props ->
                    Mui.textField [
                      yield! props.felizProps
                      textField.variant.outlined
                      textField.margin.normal
                      textField.fullWidth true
                      textField.label "Call type"
                      textField.onChange (SetServiceCallReasonText >> dispatch)
                    ]
                  )
                  autocomplete.value (
                    model.RequestInfo.ServiceCallReason
                    |> Option.bind (fun (reason) -> availableCallTypes |> List.tryFind (fun x -> x = reason))
                  )
                  autocomplete.onChange (SelectServiceCallReason >> dispatch)
                ]
                Mui.textField [
                  textField.placeholder "Describe the type of service needed"
                  textField.variant.outlined
                  textField.multiline true
                  textField.rows 4
                  textField.margin.normal
                  textField.fullWidth true
                  textField.label "Service Request"
                  textField.required true
                  textField.onChange (fun (ev : Browser.Types.Event) -> { model.RequestInfo with Description = strOption (!!ev.target?value) |> Option.defaultValue ""} |> ServiceRequestUpdated |> dispatch)
                ]
              ]
            ]
          ]
        ]
      ]
    ]
  ]

let viewSubmitAndCancelButtons (classes : Styles.SiteClasses) (s : ServiceRequestEntryState) isLoading dispatch =
  let buttonProgressIndicator =
    Mui.circularProgress [
      prop.className classes.buttonProgress
      prop.size 24
    ]
  Mui.grid [
    grid.container true
    //grid.direction.column
    grid.spacing._2
    grid.children [
      Mui.grid [
        grid.item true
        grid.xs._6
        grid.children [
          Html.div [
            prop.className classes.wrapper
            prop.children [
              Mui.button [
                prop.type'.submit
                button.fullWidth true
                button.variant.contained
                button.color.primary
                button.children "Submit"
                button.disabled (s.RequestInfo.Description.Length = 0 || isLoading)
                prop.onClick (fun e ->
                  e.preventDefault()
                  let r =
                    { Shared.ServiceRequest.EquipmentNumber = s.Equipment.EquipmentNumber
                      Description = s.RequestInfo.Description
                      Contact = s.RequestInfo.Contact
                      ServiceCallReason = s.RequestInfo.ServiceCallReason |> Option.defaultValue ServiceCallReason.Other
                      PurchaseOrderNumber = s.RequestInfo.PurchaseOrderNumber
                    }
                  r |> PlaceRequest |> dispatch
                )
              ]
              if isLoading then buttonProgressIndicator else Html.none
            ]
          ]
        ]
      ]
      Mui.grid [
        grid.item true
        grid.xs._6
        grid.children [
          Mui.button [
            button.fullWidth true
            button.variant.contained
            button.color.default'
            button.children "Clear"
            button.disabled isLoading
            prop.onClick (fun e ->
              e.preventDefault()
              ResetState |> dispatch
            )
          ]
        ]
      ]
    ]
  ]

let pendingHeaderMessage =
  Mui.grid [
    grid.container true
    grid.direction.column
    grid.spacing._2
    grid.children [
      Mui.grid [
        grid.item true
        grid.children [
          Mui.typography [
            typography.variant.body1
            typography.children "Please fill out this form to request service."
          ]
        ]
      ]
    ]
  ]

let timelineOppositeContentStyles =
  prop.style [
    style.flexBasis (0.2)
    style.flexGrow (0)
    style.flexShrink (0)
    style.padding (0)
  ]

let viewSectionHeader (headerText : string) =
  Mui.grid [
    grid.container true
    grid.direction.column
    grid.spacing._2
    grid.children [
      Mui.grid [
        grid.item true
        grid.children [
          Mui.typography [
            typography.variant.h5
            typography.gutterBottom false
            typography.children headerText
          ]
        ]
      ]
    ]
  ]

let hideOnMobile (e : ReactElement) =
  Mui.hidden [
    hidden.smDown true
    hidden.children e
  ]

let viewServiceCallState = React.functionComponent (fun (inputProps: {| state : ServiceRequestEntryState; showWarning : bool; dispatchFn : (Msg -> unit) |}) ->
  let classes = Styles.useStyles()
  match inputProps.state.ServiceCalls with
  | None -> Html.text "Checking for pending calls..."
  | Some [] -> Html.text "No pending calls found."
  | Some calls ->
    Html.div [
      let c = calls |> List.sortByDescending (fun x -> x.Id)
      let name, email =
        match inputProps.state.RequestInfo.Contact with
        | ContactSelectionInfo.Contact v -> None, v.Email
        | ContactSelectionInfo.NewContact v -> Some (sprintf "%s %s" v.FirstName v.LastName), v.Email
      Components.ServiceCallInfo.view {| token = inputProps.state.Equipment.Token; equipmentNumber = inputProps.state.Equipment.EquipmentNumber; calls = c; customerContactName = name; customerContactEmail = email; |}

      if inputProps.showWarning then
        let msg =
          if calls.Length = 1
          then Html.text "There is a pending service call for this equipment. If you would like an ETA, please use the \"Request ETA\" button above."
          else Html.text "There are pending service calls for this equipment. If you would like an ETA, please use the \"Request ETA\" buttons above."

        Html.div [
          prop.className classes.wrapper
          prop.children [
            Mui.alert [
              alert.severity.warning
              alert.children msg
            ]
          ]
        ]
        //Html.text "Would you like to create a new service request?"
        Mui.button [
          prop.type'.submit
          button.fullWidth true
          button.variant.contained
          button.color.primary
          button.children "Add Different Request"
          prop.onClick (fun e ->
            e.preventDefault ()
            AcknowledgeExistingCalls |> inputProps.dispatchFn
          )
      ]
    ]
)

type SectionParts = { Header : ReactElement; IsActive : bool; HideOnMobile : bool; Content : ReactElement list; Icon : ReactElement; }

let equipmentIcon = printerIcon []
let contactIcon = personIcon []
let purchaseOrderNumberIcon = infoIcon []
let serviceIcon = wrenchIcon []
let requestDetailsIcon = noteTextIcon []
let orderIcon = truckFastIcon []

let equipmentHeader = viewSectionHeader "Equipment"
let pendingCallsHeader = viewSectionHeader "Pending Calls"
let contactHeader = viewSectionHeader "Contact"
let purchaseOrderNumberHeader = viewSectionHeader "PO Number"
let requestDetailsHeader = viewSectionHeader "Details"
let orderHeader = viewSectionHeader "Request Result"

let buildSections model reCaptchaSiteKey classes dispatch =
  match model with
  | EquipmentLookup ->
    [ { Header = equipmentHeader
        IsActive = true;
        HideOnMobile = false;
        Content = [ Components.EquipmentLookup.view {| reCaptchaSiteKey = reCaptchaSiteKey; equipmentOrSerialNumberOption = None; returnResult = (fun e -> e |> EquipmentLookupResult |> dispatch) |} ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = contactIcon }
      { Header = purchaseOrderNumberHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = purchaseOrderNumberIcon }
      { Header = pendingCallsHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = serviceIcon }
      { Header = requestDetailsHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = requestDetailsIcon }
      { Header = orderHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon }  ]
  | ContactEntry s ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view s.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = true;
        HideOnMobile = false;
        Content =
          match s.Contact with
          | None -> [ Components.ContactLookup.view {| token = s.Equipment.Token; returnResult = ContactEntryCompleted >> dispatch; |} ]
          | Some c -> [ Components.ContactInfo.view c ]
        Icon = contactIcon }
      { Header = purchaseOrderNumberHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = purchaseOrderNumberIcon }
      { Header = pendingCallsHeader
        IsActive = false;
        HideOnMobile = true;
        Content = [ ]
        Icon = serviceIcon }
      { Header = requestDetailsHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = requestDetailsIcon }
      { Header = orderHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon } ]
  | PurchaseOrderNumberEntry s ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view s.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.ContactInfo.view s.RequestInfo.Contact ]
        Icon = contactIcon }
      { Header = purchaseOrderNumberHeader
        IsActive = true;
        HideOnMobile = false;
        Content = [ Components.PurchaseOrderNumberEntry.view {| returnResult = PurchaseOrderNumberEntryCompleted >> dispatch |} ]
        Icon = purchaseOrderNumberIcon }
      { Header = pendingCallsHeader
        IsActive = false;
        HideOnMobile = true;
        Content = [ ]
        Icon = serviceIcon }
      { Header = requestDetailsHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = requestDetailsIcon }
      { Header = orderHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon } ]
  | ExistingCallsWarning s ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view s.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.ContactInfo.view s.RequestInfo.Contact ]
        Icon = contactIcon }
      { Header = purchaseOrderNumberHeader
        IsActive = false;
        HideOnMobile = false;
        Content =
          s.RequestInfo.PurchaseOrderNumber
          |> Option.defaultValue "N/A"
          |> (fun x -> [ Components.PurchaseOrderNumberInfo.view x ])
        Icon = purchaseOrderNumberIcon }
      { Header = pendingCallsHeader
        IsActive = true;
        HideOnMobile = false;
        Content = [ viewServiceCallState {| state = s; showWarning = true; dispatchFn = dispatch; |} ]
        Icon = serviceIcon }
      { Header = requestDetailsHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = requestDetailsIcon }
      { Header = orderHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon }  ]
  | ServiceCallLookup s ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view s.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.ContactInfo.view s.RequestInfo.Contact ]
        Icon = contactIcon }
      { Header = purchaseOrderNumberHeader
        IsActive = false;
        HideOnMobile = false;
        Content =
          s.RequestInfo.PurchaseOrderNumber
          |> Option.defaultValue "N/A"
          |> (fun x -> [ Components.PurchaseOrderNumberInfo.view x ])
        Icon = purchaseOrderNumberIcon }
      { Header = pendingCallsHeader
        IsActive = true;
        HideOnMobile = false;
        Content = [ viewServiceCallState {| state = s; showWarning = false; dispatchFn = dispatch; |} ]
        Icon = serviceIcon }
      { Header = requestDetailsHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = requestDetailsIcon }
      { Header = orderHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon }  ]
  | ServiceRequestEntry s ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view s.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.ContactInfo.view s.RequestInfo.Contact ]
        Icon = contactIcon }
      { Header = purchaseOrderNumberHeader
        IsActive = false;
        HideOnMobile = false;
        Content =
          s.RequestInfo.PurchaseOrderNumber
          |> Option.defaultValue "N/A"
          |> (fun x -> [ Components.PurchaseOrderNumberInfo.view x ])
        Icon = purchaseOrderNumberIcon }
      { Header = pendingCallsHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ viewServiceCallState {| state = s; showWarning = false; dispatchFn = dispatch; |} ]
        Icon = serviceIcon }
      { Header = requestDetailsHeader
        IsActive = true;
        HideOnMobile = false;
        Content =
          [ serviceRequestEntryView s dispatch
            viewSubmitAndCancelButtons classes s false dispatch]
        Icon = requestDetailsIcon }
      { Header = orderHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon } ]
  | PlacingServiceCall s ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view s.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.ContactInfo.view s.RequestInfo.Contact ]
        Icon = contactIcon }
      { Header = purchaseOrderNumberHeader
        IsActive = false;
        HideOnMobile = false;
        Content =
          s.RequestInfo.PurchaseOrderNumber
          |> Option.defaultValue "N/A"
          |> (fun x -> [ Components.PurchaseOrderNumberInfo.view x ])
        Icon = purchaseOrderNumberIcon }
      { Header = pendingCallsHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ viewServiceCallState {| state = s; showWarning = false; dispatchFn = dispatch; |} ]
        Icon = serviceIcon }
      { Header = requestDetailsHeader
        IsActive = true;
        HideOnMobile = false;
        Content =
          [ serviceRequestEntryView s dispatch
            viewSubmitAndCancelButtons classes s true dispatch ]
        Icon = requestDetailsIcon }
      { Header = orderHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon } ]
  | RequestResult r ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view r.ServiceRequestEntryState.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.ContactInfo.view r.ServiceRequestEntryState.RequestInfo.Contact ]
        Icon = contactIcon }
      { Header = purchaseOrderNumberHeader
        IsActive = false;
        HideOnMobile = false;
        Content =
          r.ServiceRequestEntryState.RequestInfo.PurchaseOrderNumber
          |> Option.defaultValue "N/A"
          |> (fun x -> [ Components.PurchaseOrderNumberInfo.view x ])
        Icon = purchaseOrderNumberIcon }
      { Header = pendingCallsHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ viewServiceCallState {| state = r.ServiceRequestEntryState; showWarning = false; dispatchFn = dispatch; |} ]
        Icon = serviceIcon }
      { Header = requestDetailsHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ serviceRequestEntryView r.ServiceRequestEntryState dispatch ]
        Icon = requestDetailsIcon }
      { Header = orderHeader
        IsActive = false;
        HideOnMobile = false;
        Content =
          [ Html.text (sprintf "Thank you. Your service request has been received, and you will receive a notification when your request has been processed.")
            Mui.button [
              prop.type'.submit
              button.fullWidth true
              button.variant.contained
              button.color.primary
              button.children "Add Another Request"
              prop.onClick (fun e ->
                e.preventDefault ()
                ResetState |> dispatch
              )
            ]
          ]
        Icon = orderIcon } ]
  | DowngradedForm _ ->
    []

let viewMobile (sections : SectionParts list) =
  Html.div [
    prop.children (
      sections
      |> List.filter (fun x -> not x.HideOnMobile)
      |> List.collect (fun x ->
        [
          x.Header
          yield! x.Content
        ]
      )
    )
  ]

let viewNonMobile (sections : SectionParts list) =
  Mui.timeline [
    timeline.children (
      sections
      |> List.mapi (fun i x ->
        Mui.timelineItem [
          Mui.timelineOppositeContent [
            //prop.className classes.timelineOppositeContent
            timelineOppositeContentStyles
          ]
          Mui.timelineSeparator [
            Mui.timelineDot [
              if x.IsActive then ( timelineDot.color.primary)
              timelineDot.children [ x.Icon ]
            ]
            if (i + 1) < sections.Length then
              Mui.timelineConnector [
              ]
          ]
          Mui.timelineContent [
            x.Header
            yield! x.Content
          ]
        ]
      )
    )
  ]

let view = React.functionComponent (fun (inputProps: {| equipmentOption : Equipment option; reCaptchaSiteKey : string; |}) ->
  let model, dispatch = React.useElmish(init (inputProps.equipmentOption), update, [| |])
  let classes = Styles.useStyles()
  let isMobile = Hooks.useMediaQuery (Client.Theme.defaultTheme.breakpoints.downSm)
  Html.div [
    prop.className [
      classes.standAloneContent
    ]
    prop.children [
      Mui.grid [
        grid.container true
        grid.direction.column
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.children [
              Mui.typography [
                typography.variant.h4
                typography.children "Request Service"
              ]
            ]
          ]
        ]
      ]

      match model with
      | DowngradedForm initialState -> Components.ServiceForm.view {| reCaptchaSiteKey = inputProps.reCaptchaSiteKey; initialValues = Some initialState ; returnResult = (fun _ -> dispatch ResetState); |}
      | _ ->
        pendingHeaderMessage
        let sections = buildSections model inputProps.reCaptchaSiteKey classes dispatch
        match isMobile with
        | true -> viewMobile sections
        | false -> viewNonMobile sections
    ]
  ]
)
